import { z } from 'zod';
import { safeFloat } from '@/modules/safe-float';
import { pct, ratio01 } from '@/modules/brands';
import { AddressSchema } from '@/modules/address-schema';

// Contract source: the committed OpenAPI snapshot
// (scripts/swagger-snapshot/sanr.json) — the spec, never live-response
// sampling alone: states absent from current data still ship in it.

// GET_ISSUER — the full own-profile payload.
export const ProfileSchema = z.looseObject({
  id: z.string(),
  // null = walletless SSO (email-only) account — the spec's LegacyIssuer
  // types address nullable; wallet-bound surfaces gate on it.
  address: AddressSchema.nullable(),
  username: z.string(),
  createdAt: z.string(),
  description: z.string().nullable(),
  avatar: z.string().nullable(),
  GDPR: z.string().nullable(),
  twitter: z.string().nullable(),
  telegramId: z.string().nullable(),
  followings: z.array(z.looseObject({ issuerId: z.string() })),
  // Tri-state: absent (a backend without the invite system) = fail-open,
  // treated as invited; explicit null is the ONLY uninvited signal; a
  // string is the inviter's UUID.
  invitedBy: z.string().nullable().optional(),
  // Latched completed-tutorial keys → true. Empty when none; absent
  // fail-open on backends without it.
  tutorials: z.record(z.string(), z.boolean()).optional(),
});

// The in-app user: the own-profile payload with the access token stitched
// on by the auth flow (the refresh token is persisted separately, never
// here).
export type Profile = z.output<typeof ProfileSchema> & { accessToken: string };

// The token response shared by POST_AUTH_INVITE and the SSO endpoints:
// core profile fields + tokens. Ships WITHOUT followings / telegramId —
// those arrive on the follow-up own-profile read. Same invitedBy tri-state
// as the profile.
export const AuthResponseSchema = ProfileSchema.omit({
  followings: true,
  telegramId: true,
}).extend({
  accessToken: z.string(),
  refreshToken: z.string(),
});
export type AuthResponse = z.output<typeof AuthResponseSchema>;

// GET_AUTH_ACCOUNTS — the SSO accounts signed into this browser, proxied
// server-to-server (the SSO endpoint is authorized by SSO's own httpOnly
// cookie, third-party from the app origin — unreachable client-side).
// available: false is a NORMAL state, not an error: the backend's SSO
// access token has a bounded life (no offline_access grant, and a
// refreshed token would carry the same dead browser-session binding), so
// the list stops being producible a while after login. An empty `accounts`
// with available: false means "could not ask", never "no other accounts" —
// the switch itself still works through the redirect path. reason is the
// spec-owned closed set: NO_SSO_SESSION (wallet-only account or the SSO
// session ended), EXPIRED_NO_REFRESH (aged-out token — redirect path still
// works), SSO_UNAVAILABLE (transient), SCOPE_NOT_GRANTED (client
// registration lacks the `accounts` scope — ops problem, hide the
// switcher). sub is the value POST_AUTH_SWITCH takes as targetSub. The
// wire rows also carry a `current` flag — deliberately unread: it marks
// the account the SSO SIDE considers active in this browser, which is not
// necessarily the account the app is logged in as, so the app derives its
// own-account marker from the access token's ssoUserId claim instead. The
// list is a live read: never persist it across sessions.
const SsoAccountsSchema = z.looseObject({
  available: z.boolean(),
  reason: z
    .enum([
      'NO_SSO_SESSION',
      'EXPIRED_NO_REFRESH',
      'SSO_UNAVAILABLE',
      'SCOPE_NOT_GRANTED',
    ])
    .nullable(),
  accounts: z.array(
    z.looseObject({
      sub: z.string().nullable(),
      name: z.string().nullable(),
      email: z.string().nullable(),
      picture: z.string().nullable(),
    }),
  ),
});

// GET_SSO_CONFIG — OIDC discovery for the SSO login flow. When enabled,
// issuer + clientId parameterize the /oauth/authorize redirect and scopes
// is the set the frontend must request there — the spec requires it on the
// enabled variant and both deployments ship it.
const SsoConfigSchema = z.union([
  z.looseObject({
    enabled: z.literal(true),
    issuer: z.string(),
    clientId: z.string(),
    scopes: z.array(z.string()),
  }),
  z.looseObject({ enabled: z.literal(false) }),
]);
export type SsoConfig = z.output<typeof SsoConfigSchema>;

// Legacy key-market subscription row — the user's holdings of an issuer's
// keys (the pre-zk key market). Ships unconditionally — empty array when the
// user holds none. sharesBalance = keys held; issuer.subscriptionsSupply =
// the issuer's total key supply (both integer counts; an issuer's last key
// is unsellable, so supply === balance means one key must remain).
// The spec types the subscription items as bare objects, so the field set
// follows the live payload. Counts ship as numbers; safeFloat also covers a
// numeric-string regression.
const SubscriptionSchema = z.looseObject({
  id: z.string(),
  sharesBalance: safeFloat,
  issuer: z.looseObject({
    address: AddressSchema,
    username: z.string(),
    avatar: z.string().nullable(),
    subscriptionsSupply: safeFloat,
  }),
});
export type Subscription = z.output<typeof SubscriptionSchema>;

// Issuer/player row — GET_TOTAL_ISSUERS items; GET_USER_INFO extends it.
const UserSchema = z.looseObject({
  id: z.string(),
  // null = walletless SSO account (same LegacyIssuer nullability as the
  // profile's address).
  address: AddressSchema.nullable(),
  username: z.string(),
  avatar: z.string().nullable(),
  description: z.string().nullable(),
  twitter: z.string().nullable(),
  createdAt: z.string(),
  lastActivityDate: z.string().nullable(),
  // Backend-computed metrics, rendered as-is — only their units (a
  // percentage, a ratio) are relied on; don't build logic on how the
  // backend derives them.
  marketPredictionUpPercent: pct.nullable(),
  beatKOLs: ratio01.nullable(),
  notesAboutThisIssuer: z.array(z.looseObject({ text: z.string() })).optional(),
  subscriptions: z.array(SubscriptionSchema),
});
export type User = z.output<typeof UserSchema>;

// The issuer include is the full legacy-issuer object — richer than the
// spec's slim issuer summary, which is why the declared include fields are
// required (a slimmed-down include degrades the parse);
// sumPerformanceClosed is null until a signal closed. notesAboutThisIssuer
// is the viewer-scoped private-note relation — present only on authed
// reads.
export const UserInfoSchema = UserSchema.extend({
  followings: z.array(
    z.looseObject({
      id: z.string(),
      issuer: z.looseObject({
        id: z.string(),
        username: z.string(),
        address: AddressSchema.nullable(),
        avatar: z.string().nullable(),
        createdAt: z.string(),
        sumPerformanceClosed: ratio01.nullable(),
        // unread-field-allow: consumed structurally — the issuer object passes whole into app-side UI with its own prop types
        notesAboutThisIssuer: z
          .array(z.looseObject({ text: z.string() }))
          .optional(),
      }),
    }),
  ),
});
export type UserInfo = z.output<typeof UserInfoSchema>;

const ListUsersSchema = z.looseObject({
  data: z.array(UserSchema),
});

// Invite-code row — shared by GET_INVITE_CODES items,
// POST_INVITE_CODES_GENERATE, and PUT_INVITE_CODE_NOTE. Of the declared
// fields the Swagger spec requires only id + code, hence the optionals.
export const InviteCodeSchema = z.looseObject({
  id: z.string(), // UUID — the note-update endpoint's url param
  // Backend-generated — link-embedded (URL-encoded at build) and rendered
  // as-is; don't build logic on its format.
  code: z.string(),
  // Redeemer profile; null while the code is unused. Bare wallets (no
  // registered profile) ship username/avatar null — display fallbacks are
  // the consumer's concern.
  usedBy: z
    .looseObject({
      username: z.string().nullable().optional(),
      avatar: z.string().nullable().optional(),
      address: AddressSchema.nullable().optional(),
    })
    .nullable()
    .optional(),
  note: z.string().nullable().optional(), // freeform, ≤250 chars
  usedAt: z.string().nullable().optional(), // ISO date-time; null while unused
  createdAt: z.string().optional(), // ISO date-time
});
export type InviteCode = z.output<typeof InviteCodeSchema>;

// The id → zod schema registry: every endpoint id fetchApi accepts, each
// with the response shape it resolves with — EndpointResponses derives from
// it, so the shapes cannot drift from the implementation.
export const endpointSchemas = {
  POST_AUTH_INVITE: AuthResponseSchema,
  GET_SSO_CONFIG: SsoConfigSchema,
  // Body: {code, codeVerifier, redirectUri} — redirectUri must byte-match
  // the one sent to /oauth/authorize or SSO rejects the exchange (400).
  // Finds-or-creates the issuer; the AuthResponse token payload.
  POST_SSO_EXCHANGE: AuthResponseSchema,
  // Bearer-authed; accepted before the invite gate. Always 200 (apart from
  // 401/501/502) — branch on `available`, never on an HTTP error.
  GET_AUTH_ACCOUNTS: SsoAccountsSchema,
  // Bearer-authed switch to another SSO account. Body: {targetSub,
  // redirectUri, returnTo?} — redirectUri must be a redirect URI
  // registered on the SSO OAuth client, or SSO rejects the authorize
  // request; returnTo is server-filtered to same-origin and echoed back by
  // the callback endpoint. switched: true means the backend already held a
  // usable SSO session for the target and the response IS the full token
  // payload for the new account — no redirect. switched: false hands back
  // a prompt=none authorize URL the browser must TOP-LEVEL navigate to
  // (the SSO cookie never travels in a frame); its return leg lands on
  // redirectUri with ?code&state (or ?error=login_required and friends
  // when silent auth needs interaction). The previous account's SSO
  // session is deliberately NOT revoked — Google-style: switching back is
  // another switch, not a fresh login. 400 SSO_ACCOUNT_NOT_KNOWN = the live
  // list was available and lacks targetSub.
  POST_AUTH_SWITCH: z.union([
    z.looseObject({ switched: z.literal(false), redirectUrl: z.string() }),
    AuthResponseSchema.extend({ switched: z.literal(true) }),
  ]),
  // Completes the redirect leg of a switch. NO bearer — the single-use
  // server-held state row plus the server-held PKCE verifier authenticate
  // it (the sso/exchange trust model). 400s: unknown/consumed state,
  // >10min-old state (SWITCH_STATE_EXPIRED), or SSO authenticated a
  // different account than requested (login_hint is only a hint — the sub
  // is verified server-side). returnTo is what survived the switch call's
  // same-origin filter, null otherwise.
  POST_AUTH_SWITCH_CALLBACK: AuthResponseSchema.extend({
    returnTo: z.string().nullable().optional(),
  }),
  GET_ISSUER: ProfileSchema,
  // Deliberately no field contract — don't read the body, so legacy data
  // drift can't page schema-mismatch reports.
  GET_USER_FORECASTS: z.unknown(),
  GET_USER_INFO: UserInfoSchema,
  GET_TOTAL_ISSUERS: ListUsersSchema,
  // A server-minted one-time code, fit for an OAuth `state` parameter (the
  // Twitter link flow carries it as one).
  GET_AUTH: z.looseObject({ code: z.string() }),
  POST_AUTH_REFRESH: z.looseObject({ accessToken: z.string() }),
  // Bearer-authed, no body; accepted before the invite gate. The stateless
  // access token is NOT revoked server-side — the client dropping it IS
  // the logout. ssoLogoutUrl is the OIDC end-session URL for SSO-linked
  // sessions (null otherwise); ending the SSO cookie requires the browser
  // to TOP-LEVEL navigate there — the cookie is httpOnly SameSite=Lax, so
  // a background fetch of that URL cannot end the session.
  POST_AUTH_LOGOUT: z.looseObject({ ssoLogoutUrl: z.string().nullable() }),
  // Bearer-authed. Body: {addresses} — EVM addresses in any casing, at most
  // 500 (400 TOO_MANY_ADDRESSES past that). Resolves each to the profile
  // owning it through EITHER the account's primary address or a wallet SSO
  // has linked to it (disconnected ones included — a released address stays
  // reserved for the account that first proved control of it), so this is
  // the lookup for addresses that only appear as a linked wallet; an issuer
  // listing filtered by primary address misses those. Keys echo the sent
  // strings byte-for-byte — send lowercase to read back lowercase. Unknown
  // and malformed addresses are ABSENT from the answer, never null-valued:
  // key presence is the resolved/unknown signal. username is null for an
  // account that has none yet.
  POST_ISSUERS_RESOLVE: z.record(
    z.string(),
    z.looseObject({
      username: z.string().nullable(),
      avatar: z.string().nullable(),
    }),
  ),
  PUT_ISSUER: z.looseObject({}),
  // Mark-and-forget: deliberately no field contract — read tutorial state
  // from the profile after a refetch, not from this body.
  // Marks are permanent — the API has no un-mark: the request body is
  // `{tutorialKey}` only, no DELETE endpoint exists, and PUT_ISSUER's field
  // whitelist excludes `tutorials`.
  POST_ISSUER_TUTORIALS: z.unknown(),
  // Contract addresses grouped by name — one array entry per chain;
  // consumers resolve a name to THE deployed address from its entries, so
  // entry multiplicity and order are load-bearing for contract reads.
  GET_SMARTS: z.record(
    z.string(),
    z.array(z.looseObject({ address: AddressSchema })),
  ),
  GET_TELEGRAM_AUTH: z.looseObject({ telegramAuthCode: z.string() }),
  // Invite codes are caller-scoped and bearer-authed: the server returns
  // only the authenticated user's codes and 401s token-less or
  // not-yet-invited callers. Listing queryParams: `take` is REQUIRED
  // (1..500), `skip` optional, `status` optional (`used` | `unused`, omit
  // for all), `sort` optional comma-separated `field:DESC,field2:ASC`
  // (NULLs last for DESC, first for ASC).
  GET_INVITE_CODES: z.looseObject({
    data: z.array(InviteCodeSchema),
  }),
  // Optional body: `note` ≤250 chars, attached to the generated code.
  // Resolves with the newly generated rows — an ARRAY even for the
  // server-default single code (the registered array schema depends on
  // that); consumers treat the listing refetch, not this body, as the
  // source of truth.
  POST_INVITE_CODES_GENERATE: z.array(InviteCodeSchema),
  // urlParams[0] = the code's id (never the code string). Body note: a
  // string sets it, null clears it. Resolves with the row — backend-owned
  // contents; consumers don't build on this body (the listing refetch is
  // the source of truth).
  PUT_INVITE_CODE_NOTE: InviteCodeSchema,
  PUT_NOTIFICATION: z.array(z.unknown()),
  PUT_NOTIFICATIONS_READ: z.array(z.unknown()),
  POST_DELETE_NOTIFICATIONS: z.looseObject({}),
  POST_DELETE_ALL_NOTIFICATIONS: z.looseObject({}),
  POST_FOLLOWINGS: z.looseObject({}),
  DELETE_FOLLOWINGS: z.looseObject({}),
  POST_ISSUER_GDPR: z.looseObject({}),
  POST_NOTE_USER: z.looseObject({}),
  GET_NOTIFICATIONS: z.looseObject({
    data: z.array(
      z.looseObject({
        id: z.string(),
        type: z.string(),
        date: z.string(),
        read: z.boolean(),
        fromWatchlist: z.boolean(),
        // Per-type payload — only the prediction shape is declared (numeric
        // stats coerce; openPriceIndex is the legacy triggered-order
        // marker, truthiness distinguishes trigger-opened from
        // market-opened).
        details: z.looseObject({
          prediction: z
            .looseObject({
              symbol: z.string().optional(),
              leverage: safeFloat.optional(),
              openPrice: safeFloat.nullable().optional(),
              performance: safeFloat.nullable().optional(),
              openPriceIndex: z.unknown().optional(),
              issuer: z
                .looseObject({ username: z.string().nullable().optional() })
                .optional(),
            })
            .optional(),
        }),
      }),
    ),
    total: z.number(),
    unreadTotal: z.number(),
  }),
  // Rows are legacy issuers with the per-source notification-disable
  // relation; an entry's `source` names the disabled channel, and a null
  // source disables nothing. The spec's NotificationsStatusResponse omits
  // the relation and the endpoint is caller-scoped (not publicly
  // sampleable), so the field set follows the live shape.
  GET_NOTIFICATIONS_STATUS: z.looseObject({
    data: z.array(
      z.looseObject({
        id: z.string(),
        username: z.string(),
        address: AddressSchema.nullable(),
        avatar: z.string().nullable(),
        notificationDisabled: z.array(
          z.looseObject({ source: z.string().nullable() }),
        ),
        notesAboutThisIssuer: z
          .array(z.looseObject({ text: z.string() }))
          .optional(),
      }),
    ),
    total: z.number(),
  }),
  // Opaque ABI blob passed straight to viem.
  GET_ABI: z.unknown(),
};

export type EndpointId = keyof typeof endpointSchemas;

export type EndpointResponses = {
  [K in EndpointId]: z.output<(typeof endpointSchemas)[K]>;
};
